/**
 * Fetching `GET /me/permissions`.
 *
 * Lives in the app rather than in `core-access` on purpose: that package
 * evaluates permissions and deliberately knows nothing about HTTP, which is
 * what lets it be tested against a table of cases with no server anywhere.
 * Where the payload comes from is a composition decision, and this is the
 * composition root.
 */

import { useEffect, useState } from "react";
import type { PermissionsPayload } from "@hardauto/core-access";
import { NetworkError, type ApiClient } from "@hardauto/core-api";

export interface PermissionsState {
  permissions: PermissionsPayload | null;
  /** False while the request is in flight. Gates hold rather than deny. */
  ready: boolean;
}

export function usePermissions(client: ApiClient, enabled: boolean): PermissionsState {
  /**
   * The state carries the `enabled` it was computed FOR.
   *
   * Without that, there is one render after sign-in where `enabled` has become
   * true but the effect has not run yet, so the hook still reports the
   * anonymous answer — `ready: true` with no permissions. Effects run after
   * render, and one render is enough: the router mounted in that window,
   * judged `/admin/users` against an empty snapshot, and redirected a
   * permitted administrator to "forbidden". It looked exactly like a
   * permissions bug, while the menu beside it linked to the same page.
   *
   * Comparing here makes the staleness impossible to observe: a change to
   * `enabled` is not ready until the fetch for THAT value has resolved.
   */
  const [state, setState] = useState<{
    permissions: PermissionsPayload | null;
    ready: boolean;
    forEnabled: boolean;
  }>({ permissions: null, ready: false, forEnabled: enabled });

  useEffect(() => {
    if (!enabled) {
      // Not merely "no permissions" — `ready: true` as well, because for an
      // anonymous visitor the answer is known and complete. Leaving `ready`
      // false would make the access gate hold every navigation forever.
      setState({ permissions: null, ready: true, forEnabled: false });
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const payload = await client.request<PermissionsPayload>(
          client.authenticatedPath("/me/permissions"),
        );
        if (!cancelled) setState({ permissions: payload, ready: true, forEnabled: true });
      } catch (error) {
        if (cancelled) return;
        // Fail closed: no permissions, but READY, so the gates refuse rather
        // than hold. A user stuck on a spinner cannot even reach the page that
        // would tell them what went wrong; one shown "forbidden" can sign out
        // and back in.
        //
        // A network failure is the exception worth noting: it is transient, so
        // the console line matters more than the state does.
        if (error instanceof NetworkError) {
          console.warn("Could not load permissions; continuing with none.", error);
        }
        setState({ permissions: null, ready: true, forEnabled: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, enabled]);

  return {
    permissions: state.forEnabled === enabled ? state.permissions : null,
    ready: state.forEnabled === enabled && state.ready,
  };
}
